import { useState, useEffect } from 'react';

import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';

import ruLocale from 'date-fns/locale/ru';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { openDownloadWindow } from '../../../../utils/openDownloadWindow';
import { handleHttpErrorStatus } from '../../../../utils/handleHttpErrorStatus';
import { labelStyles } from '../../../../common/styles/formStyles';
import formatISODate from '../../utils/formatISODate';
import Grid from '@mui/material/Grid';
import {hetch} from "../../../../utils/fetch.hooked";

function EditCertForm({ keyId, keyName, startDate, expireDate, status, triggerRefetch, defaultKeyName, handleClose }) {
  const [isFetching, setIsFetching] = useState(false);
  const [keysList, setKeysList] = useState([]);
  const [formValues, setFormValues] = useState({keyName: defaultKeyName, startDate: new Date(startDate)});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (evt) => {
    const { name, value } = evt.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleDateChange = (startDate) => {
    setFormValues({
      ...formValues,
      startDate
    });
  }

  const handleSubmit = (evt) => {
    evt.preventDefault();
    setSubmitted(true);
    setError('');

    if (Object.values(formValues).every((value) => value)) {
      const formData = new FormData();
      formData.append('keyDtoJson', JSON.stringify({
        key_name: formValues.keyName,
        start_date: formValues.startDate ? formatISODate(formValues.startDate) : null,
      }));

      setIsFetching(true);
      hetch(`/api/keys/${keyId}`, {
        method: 'PATCH',
        body: formData,
      })
        .then(handleHttpErrorStatus)
        .then(res => res.json())
        .then(() => {
          handleClose();
          triggerRefetch();
        })
        .catch(err => err.json())
        .then(res => {
          if (res) {
            setError(res.message);
          }
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  };

  useEffect(() => {
    const fetchData = () => {
      hetch('/api/key_names')
        .then(res => res.json())
        .then(res => {
          setKeysList(res?.content ?? []);
        });
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (keysList.length && !formValues.keyName) {
      setFormValues({
        ...formValues,
        keyName
      });
    }
  }, [keysList.length, formValues, keyName]);

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {error ? (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <InputLabel
            htmlFor='select-name'
            sx={labelStyles}
            required={true}
          >
            Имя УКЭП
          </InputLabel>
          <Select
            id='select-name'
            name='keyName'
            size="small"
            fullWidth
            disabled={defaultKeyName || isFetching}
            error={submitted && !formValues.keyName}
            value={formValues.keyName}
            onChange={handleInputChange}
          >
            {keysList.map(item => (
              <MenuItem
                sx={{ fontSize: '0.875rem' }}
                key={item.id}
                value={item.key_name}
              >
                {item.key_name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12}>
          <InputLabel
            sx={labelStyles}
          >
            Файл сертификата
          </InputLabel>
          <Link
            component='button'
            variant='button'
            type='button'
            sx={{fontWeight: 'initial', textTransform: 'initial'}}
            onClick={() => openDownloadWindow(`/api/keys/${keyId}/public-key`)}
          >
            Скачать
          </Link>
        </Grid>

        <Grid item xs={12}>
          <InputLabel
            htmlFor='start-date'
            sx={labelStyles}
            required={true}
          >
            Дата начала действия сертификата
          </InputLabel>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ruLocale}
          >
            <DateTimePicker
              disabled={isFetching}
              value={formValues.startDate}
              onChange={handleDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id='start-date'
                  name='startDate'
                  size="small"
                  fullWidth
                  error={submitted && !formValues.startDate}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <InputLabel
            htmlFor='expire-date'
            sx={labelStyles}
          >
            Срок действия сертификата
          </InputLabel>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ruLocale}
          >
            <DateTimePicker
              disabled={true}
              value={expireDate}
              onChange={() => {}}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id='expire-date'
                  name='expireDate'
                  size="small"
                  fullWidth
                />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <InputLabel htmlFor='status' sx={labelStyles}>
            Статус
          </InputLabel>
          <TextField
            id='status'
            name='status'
            size="small"
            fullWidth
            disabled={true}
            value={status}
          />
        </Grid>

        <Grid item xs={12} sx={{textAlign: 'center'}}>
          <Button
            title="Сохранить сертификат"
            variant='contained'
            type='submit'
            disabled={!keysList.length || isFetching}
          >
            Сохранить
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default EditCertForm;
